import {
  EventEmitter,
  AfterContentChecked,
  AfterContentInit,
  AfterViewInit,
  OnDestroy,
  Input,
  QueryList,
  ElementRef,
  ChangeDetectorRef,
  NgZone,
  Component,
  ViewEncapsulation,
  ChangeDetectionStrategy,
  ContentChildren,
  ViewChild,
  HostBinding,
  Output
} from '@angular/core';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { FocusKeyManager, FocusableOption } from '@angular/cdk/a11y';
import { ENTER, SPACE, hasModifierKey } from '@angular/cdk/keycodes';
import { ViewportRuler } from '@angular/cdk/scrolling';
import { merge, Subject, timer, fromEvent } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { Platform, normalizePassiveListenerOptions } from '@angular/cdk/platform';
import { TabLabelWrapper } from '../tab-label-wrapper/tab-label-wrapper.directive';
import { TabId } from '../tab';

/** Config used to bind passive event listeners */
const passiveEventListenerOptions = normalizePassiveListenerOptions({
  passive: true
}) as EventListenerOptions;

/**
 * The directions that scrolling can go in when the header's tabs exceed the header width. 'After'
 * will scroll the header towards the end of the tabs list and 'before' will scroll towards the
 * beginning of the list.
 */
export type ScrollDirection = 'after' | 'before';

/**
 * The distance in pixels that will be overshot when scrolling a tab label into view. This helps
 * provide a small affordance to the label next to it.
 */
const EXAGGERATED_OVERSCROLL = 60;

/**
 * Amount of milliseconds to wait before starting to scroll the header automatically.
 * Set a little conservatively in order to handle fake events dispatched on touch devices.
 */
const HEADER_SCROLL_DELAY = 650;

/**
 * Interval in milliseconds at which to scroll the header
 * while the user is holding their pointer.
 */
const HEADER_SCROLL_INTERVAL = 100;

/** Item inside a paginated tab header. */
export type TabHeaderItem = FocusableOption & { elementRef: ElementRef; id: TabId };

@Component({
  selector: 'avano-tabs-header',
  templateUrl: './tabs-header.component.html',
  styleUrls: ['./tabs-header.component.scss'],
  encapsulation: ViewEncapsulation.None,
  // eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
  changeDetection: ChangeDetectionStrategy.Default
})
export class TabHeaderComponent implements AfterViewInit, AfterContentInit, AfterContentChecked, OnDestroy {
  @ContentChildren(TabLabelWrapper, { descendants: false }) headerItems: QueryList<TabLabelWrapper>;
  @ViewChild('headersContainer', { static: true }) headersContainer: ElementRef;
  @ViewChild('fixedTabsContainer', { static: true }) fixedTabsContainer: ElementRef;
  @ViewChild('tabListContainer', { static: true }) tabListContainer: ElementRef;
  @ViewChild('tabList', { static: true }) tabList: ElementRef;
  @ViewChild('tabListInner', { static: true }) tabListInner: ElementRef;
  @ViewChild('nextPaginator') nextPaginator: ElementRef<HTMLElement>;
  @ViewChild('previousPaginator') previousPaginator: ElementRef<HTMLElement>;

  @HostBinding('class.tab-header-pagination-controls-enabled') get showPaginationControls(): boolean {
    return this._showPaginationControls;
  }

  /** Event emitted when the tab is closed from context menu */
  @Output() readonly tabClosed: EventEmitter<TabId> = new EventEmitter();

  /** Event emitted when the option is selected. */
  @Output() readonly selectFocusedIndex: EventEmitter<TabId> = new EventEmitter<TabId>();

  /** Event emitted when a label is focused. */
  @Output() readonly indexFocused: EventEmitter<TabId> = new EventEmitter<TabId>();

  /**
   * Whether pagination should be disabled. This can be used to avoid unnecessary
   * layout recalculations if it's known that pagination won't be required.
   */
  @Input()
  get disablePagination(): boolean {
    return this._disablePagination;
  }
  set disablePagination(value: boolean) {
    this._disablePagination = coerceBooleanProperty(value);
  }

  /** The focus id of the active tab */
  @Input()
  get focusId(): TabId {
    return this._focusId;
  }
  set focusId(value: TabId) {
    this._focusId = value;
    const found = this.headerItems?.toArray().findIndex(({ id }: TabHeaderItem) => id === value);
    if (found !== -1) {
      this.focusIndex = found;
    }
  }

  /** The id of the active tab */
  @Input()
  get selectedId(): TabId {
    return this._selectedId;
  }
  set selectedId(value: TabId) {
    this._selectedId = value;
    this._selectedIdChanged = true;
  }

  /** The index of the active tab. */
  get selectedIndex(): number {
    return this._selectedIndex;
  }
  set selectedIndex(value: number) {
    value = coerceNumberProperty(value);

    if (this._selectedIndex != value) {
      this._selectedIndexChanged = true;
      this._selectedIndex = value;

      if (this._keyManager) {
        this._keyManager.updateActiveItem(value);
      }
    }
  }

  /** Tracks which element has focus; used for keyboard navigation */
  get focusIndex(): number {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this._keyManager ? this._keyManager.activeItemIndex! : 0;
  }

  /** When the focus index is set, we must manually send focus to the correct label */
  set focusIndex(value: number) {
    if (!this.isValidIndex(value) || this.focusIndex === value || !this._keyManager) {
      return;
    }

    this._keyManager.setActiveItem(value);
  }

  /** Sets the distance in pixels that the tab header should be transformed in the X-axis. */
  get scrollDistance(): number {
    return this._scrollDistance;
  }
  set scrollDistance(value: number) {
    this.scrollTo(value);
  }

  /** Whether the controls for pagination should be displayed */
  _showPaginationControls = false;

  /** Whether the tab list can be scrolled more towards the end of the tab label list. */
  disableScrollAfter = true;

  /** Whether the tab list can be scrolled more towards the beginning of the tab label list. */
  disableScrollBefore = true;

  isMenuOpen = false;

  private _focusId: TabId;
  private _selectedId: TabId;
  private _selectedIndex = 0;
  private _disablePagination = false;

  /** Emits when the component is destroyed. */
  private readonly _destroyed = new Subject<void>();

  /** The distance in pixels that the tab labels should be translated to the left. */
  private _scrollDistance = 0;

  /** Whether the header should scroll to the selected index after the view has been checked. */
  private _selectedIndexChanged = false;

  private _selectedIdChanged = false;

  /**
   * The number of tab labels that are displayed on the header. When this changes, the header
   * should re-evaluate the scroll position.
   */
  private _tabLabelCount: number;

  /** Whether the scroll distance has changed and should be applied after the view is checked. */
  private _scrollDistanceChanged: boolean;

  /** Used to manage focus between the tabs. */
  private _keyManager: FocusKeyManager<TabHeaderItem>;

  /** Cached text content of the header. */
  private _currentTextContent: string;

  /** Stream that will stop the automated scrolling. */
  private _stopScrolling = new Subject<void>();

  constructor(
    private elementRef: ElementRef,
    private changeDetectorRef: ChangeDetectorRef,
    private viewportRuler: ViewportRuler,
    private ngZone: NgZone,
    private platform: Platform
  ) {
    // Bind the `mouseleave` event on the outside since it doesn't change anything in the view.
    ngZone.runOutsideAngular(() => {
      fromEvent(elementRef.nativeElement, 'mouseleave')
        .pipe(takeUntil(this._destroyed))
        .subscribe(() => {
          this.stopInterval();
        });
    });
  }

  ngAfterViewInit(): void {
    // We need to handle these events manually, because we want to bind passive event listeners.
    fromEvent(this.previousPaginator.nativeElement, 'touchstart', passiveEventListenerOptions)
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        this.handlePaginatorPress('before');
      });

    fromEvent(this.nextPaginator.nativeElement, 'touchstart', passiveEventListenerOptions)
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        this.handlePaginatorPress('after');
      });
  }

  ngAfterContentInit(): void {
    const resize = this.viewportRuler.change(150);
    const realign = () => {
      this.updatePagination();
    };

    this._keyManager = new FocusKeyManager<TabHeaderItem>(this.headerItems).withHorizontalOrientation('ltr').withHomeAndEnd().withWrap();

    this._keyManager.updateActiveItem(this._selectedIndex);

    // Defer the first call in order to allow for slower browsers to lay out the elements.
    // This helps in cases where the user lands directly on a page with paginated tabs.
    // Note that we use `onStable` instead of `requestAnimationFrame`, because the latter
    // can hold up tests that are in a background tab.
    this.ngZone.onStable.pipe(take(1)).subscribe(realign);

    // On window resize or items change, update the orientation of
    // the key manager if the direction has changed.
    merge(resize, this.headerItems.changes)
      .pipe(takeUntil(this._destroyed))
      .subscribe(() => {
        // We need to defer this to give the browser some time to recalculate
        // the element dimensions. The call has to be wrapped in `NgZone.run`,
        // because the viewport change handler runs outside of Angular.
        this.ngZone.run(() => Promise.resolve().then(realign));
        this._keyManager.withHorizontalOrientation('ltr');
      });

    // If there is a change in the focus key manager we need to emit the `indexFocused`
    // event in order to provide a public event that notifies about focus changes. Also we realign
    // the tabs container by scrolling the new focused tab into the visible section.
    this._keyManager.change.pipe(takeUntil(this._destroyed)).subscribe((newFocusIndex: number) => {
      this.indexFocused.emit(this.headerItems.get(newFocusIndex).id);
      this.setTabFocus(newFocusIndex);
    });
  }

  ngAfterContentChecked(): void {
    // If the number of tab labels have changed, check if scrolling should be enabled
    if (this._tabLabelCount != this.headerItems.length) {
      this.updatePagination();
      this._tabLabelCount = this.headerItems.length;
      this.changeDetectorRef.markForCheck();
    }

    if (this._selectedIdChanged) {
      const found = this.headerItems?.toArray().findIndex(({ id }: TabHeaderItem) => id == this.selectedId);
      if (found !== -1) {
        this.selectedIndex = found;
        this._selectedIdChanged = false;
      }
    }

    // If the selected index has changed, scroll to the label and check if the scrolling controls
    // should be disabled.
    if (this._selectedIndexChanged) {
      this.scrollToLabel(this._selectedIndex);
      this.checkScrollingControls();
      this._selectedIndexChanged = false;
      this.changeDetectorRef.markForCheck();
    }

    // If the scroll distance has been changed (tab selected, focused, scroll controls activated),
    // then translate the header to reflect this.
    if (this._scrollDistanceChanged) {
      this.updateTabScrollPosition();
      this._scrollDistanceChanged = false;
      this.changeDetectorRef.markForCheck();
    }
  }

  /** Handles keyboard events on the header. */
  handleKeydown(event: KeyboardEvent): void {
    // We don't handle any key bindings with a modifier key.
    if (hasModifierKey(event)) {
      return;
    }

    switch (event.keyCode) {
      case ENTER:
      case SPACE:
        if (this.focusIndex !== this.selectedIndex) {
          const found = this.headerItems.get(this.focusIndex);
          if (found) {
            this.selectFocusedIndex.emit(found.id);
          }
          event.preventDefault();
        }
        break;
      default:
        this._keyManager.onKeydown(event);
    }
  }

  /** Handles click events on the pagination arrows. */
  handlePaginatorClick(direction: ScrollDirection): void {
    this.stopInterval();
    this.scrollHeader(direction);
  }

  /** Select tab from the context menu, toggles the menu and changes current index, emits that selected tab has changed */
  selectTab(tabId: TabId): void {
    this.selectFocusedIndex.emit(tabId);
    this.isMenuOpen = false;
    const found = this.headerItems?.toArray().findIndex(({ id }: TabHeaderItem) => id === tabId);
    if (found !== -1) {
      this.selectedIndex = found;
    }
  }

  /**
   * Handles the user pressing down on one of the paginators.
   * Starts scrolling the header after a certain amount of time.
   * @param direction In which direction the paginator should be scrolled.
   */
  handlePaginatorPress(direction: ScrollDirection, mouseEvent?: MouseEvent): void {
    // Don't start auto scrolling for right mouse button clicks. Note that we shouldn't have to
    // null check the `button`, but we do it so we don't break tests that use fake events.
    if (mouseEvent && mouseEvent.button != null && mouseEvent.button !== 0) {
      return;
    }

    // Avoid overlapping timers.
    this.stopInterval();

    // Start a timer after the delay and keep firing based on the interval.
    timer(HEADER_SCROLL_DELAY, HEADER_SCROLL_INTERVAL)
      // Keep the timer going until something tells it to stop or the component is destroyed.
      .pipe(takeUntil(merge(this._stopScrolling, this._destroyed)))
      .subscribe(() => {
        const { maxScrollDistance, distance } = this.scrollHeader(direction);

        // Stop the timer if we've reached the start or the end.
        if (distance === 0 || distance >= maxScrollDistance) {
          this.stopInterval();
        }
      });
  }

  /**
   * Callback for when the MutationObserver detects that the content has changed.
   */
  onContentChanges(): void {
    const textContent = this.elementRef.nativeElement.textContent;

    // We need to diff the text content of the header, because the MutationObserver callback
    // will fire even if the text content didn't change which is inefficient and is prone
    // to infinite loops if a poorly constructed expression is passed in.
    if (textContent !== this._currentTextContent) {
      this._currentTextContent = textContent || '';

      // The content observer runs outside the `NgZone` by default, which
      // means that we need to bring the callback back in ourselves.
      this.ngZone.run(() => {
        this.updatePagination();
        this.changeDetectorRef.markForCheck();
      });
    }
  }

  /**
   * Updates the view whether pagination should be enabled or not.
   *
   * WARNING: Calling this method can be very costly in terms of performance. It should be called
   * as infrequently as possible from outside of the Tabs component as it causes a reflow of the
   * page.
   */
  updatePagination(): void {
    this.checkPaginationEnabled();
    this.checkScrollingControls();
    this.updateTabScrollPosition();
  }

  /** Scrolls to current label, public method but should be used with caution as it requires many DOM calculations */
  scrollToCurrentLabel(): void {
    this.scrollToLabel(this._selectedIndex);
  }

  /**
   * Sets focus on the HTML element for the label wrapper and scrolls it into the view if
   * scrolling is enabled.
   */
  setTabFocus(tabIndex: number): void {
    if (this._showPaginationControls) {
      this.scrollToLabel(tabIndex);
    }

    if (this.headerItems && this.headerItems.length) {
      this.headerItems.get(tabIndex).focus();

      // Do not let the browser manage scrolling to focus the element, this will be handled
      // by using translation. The scroll left should be 0.
      const containerEl = this.tabListContainer.nativeElement;

      containerEl.scrollLeft = 0;
    }
  }

  /** Stops the currently-running paginator interval.  */
  stopInterval(): void {
    this._stopScrolling.next();
  }

  ngOnDestroy(): void {
    this._destroyed.next();
    this._destroyed.complete();
    this._stopScrolling.complete();
  }

  /**
   * Determines if an index is valid.  If the tabs are not ready yet, we assume that the user is
   * providing a valid index and return true.
   */
  private isValidIndex(index: number): boolean {
    if (!this.headerItems) {
      return true;
    }

    const tab = this.headerItems ? this.headerItems.toArray()[index] : null;
    return !!tab;
  }

  /** Performs the CSS transformation on the tab list that will cause the list to scroll. */
  private updateTabScrollPosition(): void {
    if (this.disablePagination) {
      return;
    }

    const scrollDistance = this.scrollDistance;
    const translateX = -scrollDistance;

    // We round the `transform` here, because transforms with sub-pixel precision cause some
    // browsers to blur the content of the element.
    this.tabList.nativeElement.style.transform = `translateX(${Math.round(translateX)}px)`;

    // Setting the `transform` on IE will change the scroll offset of the parent, causing the
    // position to be thrown off in some cases. We have to reset it ourselves to ensure that
    // it doesn't get thrown off.
    if (this.platform.TRIDENT || this.platform.EDGE) {
      this.tabListContainer.nativeElement.scrollLeft = 0;
    }
  }

  /**
   * Moves the tab list in the 'before' or 'after' direction (towards the beginning of the list or
   * the end of the list, respectively). The distance to scroll is computed to be a third of the
   * length of the tab list view window.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private scrollHeader(direction: ScrollDirection): { maxScrollDistance: number; distance: number } {
    const viewLength = this.tabListContainer.nativeElement.offsetWidth;

    // Move the scroll distance one-third the length of the tab list's viewport.
    const scrollAmount = ((direction == 'before' ? -1 : 1) * viewLength) / 3;

    return this.scrollTo(this._scrollDistance + scrollAmount);
  }

  /**
   * Moves the tab list such that the desired tab label (marked by index) is moved into view.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private scrollToLabel(labelIndex: number): void {
    if (this.disablePagination) {
      return;
    }

    const selectedLabel = this.headerItems ? this.headerItems.toArray()[labelIndex] : null;

    if (!selectedLabel) {
      return;
    }

    // The view length is the visible width of the tab labels.
    const viewLength = this.tabListContainer.nativeElement.offsetWidth;
    const { offsetLeft, offsetWidth } = selectedLabel.elementRef.nativeElement;

    const labelBeforePos: number = offsetLeft;
    const labelAfterPos: number = labelBeforePos + offsetWidth;

    const beforeVisiblePos = this.scrollDistance;
    const afterVisiblePos = this.scrollDistance + viewLength;

    if (labelBeforePos < beforeVisiblePos) {
      // Scroll header to move label to the before direction
      this.scrollDistance -= beforeVisiblePos - labelBeforePos + EXAGGERATED_OVERSCROLL;
    } else if (labelAfterPos > afterVisiblePos) {
      // Scroll header to move label to the after direction
      this.scrollDistance += labelAfterPos - afterVisiblePos + EXAGGERATED_OVERSCROLL;
    }
  }

  /**
   * Evaluate whether the pagination controls should be displayed. If the scroll width of the
   * tab list is wider than the size of the header container, then the pagination controls should
   * be shown.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private checkPaginationEnabled(): void {
    if (this.disablePagination) {
      this._showPaginationControls = false;
    } else {
      const isEnabled =
        this.tabListInner.nativeElement.scrollWidth >
        this.headersContainer.nativeElement.offsetWidth - this.fixedTabsContainer.nativeElement.offsetWidth;

      if (!isEnabled) {
        this.scrollDistance = 0;
      }

      if (isEnabled !== this._showPaginationControls) {
        this.changeDetectorRef.markForCheck();
      }

      this._showPaginationControls = isEnabled;
    }
  }

  /**
   * Evaluate whether the before and after controls should be enabled or disabled.
   * If the header is at the beginning of the list (scroll distance is equal to 0) then disable the
   * before button. If the header is at the end of the list (scroll distance is equal to the
   * maximum distance we can scroll), then disable the after button.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private checkScrollingControls(): void {
    if (this.disablePagination) {
      this.disableScrollAfter = this.disableScrollBefore = true;
    } else {
      // Check if the pagination arrows should be activated.
      this.disableScrollBefore = this.scrollDistance == 0;
      this.disableScrollAfter = this.scrollDistance == this.getMaxScrollDistance();
      this.changeDetectorRef.markForCheck();
    }
  }

  /**
   * Determines what is the maximum length in pixels that can be set for the scroll distance. This
   * is equal to the difference in width between the tab list container and tab header container.
   *
   * This is an expensive call that forces a layout reflow to compute box and scroll metrics and
   * should be called sparingly.
   */
  private getMaxScrollDistance(): number {
    const lengthOfTabList = this.tabListInner.nativeElement.scrollWidth;
    const viewLength = this.tabListContainer.nativeElement.offsetWidth;
    return lengthOfTabList - viewLength || 0;
  }

  /**
   * Scrolls the header to a given position.
   * @param position Position to which to scroll.
   * @returns Information on the current scroll distance and the maximum.
   */
  private scrollTo(position: number): { maxScrollDistance: number; distance: number } {
    if (this.disablePagination) {
      return { maxScrollDistance: 0, distance: 0 };
    }

    const maxScrollDistance = this.getMaxScrollDistance();
    this._scrollDistance = Math.max(0, Math.min(maxScrollDistance, position));

    // Mark that the scroll distance has changed so that after the view is checked, the CSS
    // transformation can move the header.
    this._scrollDistanceChanged = true;
    this.checkScrollingControls();

    return { maxScrollDistance, distance: this._scrollDistance };
  }
}
